import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { API } from "aws-amplify";
import { format, isToday, parseJSON } from "date-fns";
import { es } from "date-fns/locale";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import { CSVLink } from "react-csv";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Alert from "@material-ui/lab/Alert";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import AllInboxIcon from "@material-ui/icons/AllInbox";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddIcon from "@material-ui/icons/Add";
import GetAppIcon from "@material-ui/icons/GetApp";
import logger from "../logger";
import { Ventas } from "./Ventas";
import { getEstadoInfo } from "./Ventas/functions";
import { AppContext } from "../views/App";

const useStyles = makeStyles(theme => ({
  backButton: {
    marginRight: theme.spacing(2)
  },
  appButton: {
    marginLeft: theme.spacing(2),
    color: "white",
    "&:hover": {
      color: "white"
    }
  },
  title: {
    flexGrow: 1
  },
  container: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    overflow: "auto"
  },
  secondaryAction: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  secondaryText: {
    textAlign: "right",
    marginRight: theme.spacing(2)
  },
  empty: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4)
  },
  dialog: {
    transform: "translateY(-75%)"
  },
  searchbox: {
    margin: theme.spacing(1, 2, 2)
  }
}));

const ventas = new Ventas();

const tipos = {
  recibidos: "recibidos",
  abandonados: "abandonados",
  archivados: "archivados"
};

const fetchTickets = async ({
  cuenta,
  tipo,
  estado,
  searchFolio,
  searchNombre,
  onStart,
  onEnd
}) => {
  onStart();

  try {
    const queryStringParameters = {
      cuenta: cuenta.id_cuenta
    };

    if (searchNombre) queryStringParameters.nombre = searchNombre;
    else if (searchFolio) queryStringParameters.folio = searchFolio;
    else if (estado) queryStringParameters.estado = estado;
    else queryStringParameters.tipo = tipo;

    const init = {
      queryStringParameters
    };

    const result = await API.get(process.env.REACT_APP_HUB_API, "/tickets/pedidos", init);

    if (result.success) {
      const items = JSON.parse(result.data.tickets);

      return ventas.parseTicketList(items);
    }
  } catch (err) {
    logger("PedidosList.fetchTickets.error", err);
    return null;
  } finally {
    onEnd();
  }
};

const PedidosList = ({ tipo, selected, onSelect, onBack, onNuevoPedido, shouldFetch }) => {
  const classes = useStyles();
  const { cuenta, store } = useContext(AppContext);
  const [openMore, setOpenMore] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [filtros, setFiltros] = useState([]);
  const [filtro, setFiltro] = useState(null);
  const [fetching, setFetching] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [searchFolio, setSearchFolio] = useState(null);
  const [searchNombre, setSearchNombre] = useState(null);
  const moreRef = useRef(null);
  const searchRef = useRef(null);

  const fetch = useCallback(async () => {
    const result = await fetchTickets({
      cuenta,
      tipo,
      estado: filtro && filtro.value,
      searchFolio,
      searchNombre,
      onStart: () => setFetching(true),
      onEnd: () => setFetching(false)
    });

    if (result) {
      setTickets(result);
    }
  }, [cuenta, filtro, searchFolio, searchNombre, tipo]);

  useEffect(() => {
    switch (tipo) {
      case tipos.recibidos:
        setFiltros(ventas.filtros.recibidos);
        break;
      case tipos.abandonados:
        setFiltros(ventas.filtros.abandonados);
        break;
      case tipos.archivados:
        setFiltros(ventas.filtros.archivados);
        break;
      default:
        break;
    }
  }, [tipo]);

  useEffect(() => {
    if (cuenta) {
      fetch();
    }
  }, [cuenta, fetch]);

  useEffect(() => {
    if (shouldFetch) {
      fetch();
    }
  }, [shouldFetch, fetch]);

  useEffect(() => {
    if (selected) {
      for (let i = 0; i < tickets.length; i++) {
        if (
          tickets[i].id_ticket === selected.id_ticket &&
          tickets[i].estado_ticket !== selected.estado_ticket
        ) {
          const aux = [...tickets];
          aux[i] = selected;

          setTickets(aux);
          break;
        }
      }
    }
  }, [selected, tickets]);

  const openMoreMenu = () => {
    setOpenMore(true);
  };

  const closeMoreMenu = () => {
    setOpenMore(false);
  };

  const openSearchDialog = () => {
    setOpenSearch(true);
  };

  const closeSearchDialog = () => {
    searchRef.current.value = "";
    setOpenSearch(false);
  };

  const selectFilter = selected => {
    setFiltro(selected);
    setSearchNombre(null);
    setSearchFolio(null);
    closeMoreMenu();
    onSelect(null);
  };

  const resetSearch = () => {
    setFetching(true);
    setTimeout(() => {
      setFiltro(null);
      setSearchNombre(null);
      setSearchFolio(null);
    }, 250);
  };

  const search = () => {
    const searchText = searchRef.current.value;

    if (searchText) {
      closeSearchDialog();
      closeMoreMenu();
      onSelect(null);

      if (isNaN(searchText)) {
        setSearchNombre(searchText);
        setSearchFolio(null);
      } else {
        setSearchNombre(null);
        setSearchFolio(searchText);
      }
    }
  };

  const searchKeyDown = event => {
    if (event.keyCode === 13) {
      event.preventDefault();
      search();
    }
  };

  const SearchDialog = () => {
    return (
      <Dialog
        fullWidth
        maxWidth="sm"
        PaperProps={{ className: classes.dialog }}
        open={openSearch}
        onClose={closeSearchDialog}
      >
        <DialogTitle>Buscar pedidos</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Ingrese el número de pedido o el nombre del cliente.
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            margin="dense"
            color="primary"
            inputProps={{
              ref: searchRef
            }}
            onKeyDown={searchKeyDown}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={closeSearchDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={search} color="primary">
            Buscar
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const appBarRecibidos = () => {
    return (
      <AppBar position="sticky">
        <Toolbar>
          {onBack && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            {filtro ? filtro.label : "Recibidos"}
          </Typography>

          {exportCsv({ tag: "recibidos" })}

          <IconButton
            ref={moreRef}
            className={classes.appButton}
            edge="end"
            color="inherit"
            onClick={onNuevoPedido}
          >
            <AddIcon />
          </IconButton>

          <IconButton className={classes.appButton} edge="end" color="inherit" onClick={fetch}>
            <RefreshIcon />
          </IconButton>

          <IconButton
            ref={moreRef}
            className={classes.appButton}
            edge="end"
            color="inherit"
            onClick={openMoreMenu}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            disableAutoFocusItem
            open={openMore}
            anchorEl={moreRef.current}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClose={closeMoreMenu}
          >
            <TextField
              className={classes.searchbox}
              placeholder="Búsqueda"
              margin="dense"
              color="primary"
              inputProps={{
                ref: searchRef
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={search}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyDown={searchKeyDown}
            />
            <Divider />
            <MenuItem selected={!filtro} onClick={() => selectFilter(null)}>
              <ListItemIcon>
                <AllInboxIcon />
              </ListItemIcon>
              <ListItemText primary="Todos" />
            </MenuItem>
            {filtros.map((f, i) => (
              <MenuItem
                key={`filtro-${i}`}
                selected={filtro && filtro.value === f.value}
                onClick={() => selectFilter(f)}
              >
                <ListItemIcon>
                  <f.icon />
                </ListItemIcon>
                <ListItemText primary={f.label} />
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>
    );
  };

  const appBarAbandonados = () => {
    return (
      <AppBar position="sticky">
        <Toolbar>
          {onBack && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            Abandonados
          </Typography>

          {exportCsv({ tag: "abandonados" })}

          <IconButton
            className={classes.appButton}
            edge="end"
            color="inherit"
            onClick={openSearchDialog}
          >
            <SearchIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
    );
  };

  const appBarArchivados = () => {
    return (
      <AppBar position="sticky">
        <Toolbar>
          {onBack && (
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onBack}
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography className={classes.title} variant="h6">
            Archivados
          </Typography>

          {exportCsv({ tag: "archivados" })}

          <IconButton
            className={classes.appButton}
            edge="end"
            color="inherit"
            onClick={openSearchDialog}
          >
            <SearchIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
    );
  };

  const currentAppBar = () => {
    switch (tipo) {
      case tipos.recibidos:
        return appBarRecibidos();
      case tipos.abandonados:
        return appBarAbandonados();
      case tipos.archivados:
        return appBarArchivados();
      default:
        return <></>;
    }
  };

  const exportCsv = ({ tag }) => {
    const headers = [
      { label: "ID_pedido", key: "id" },
      { label: "Fecha_pedido", key: "fecha" },
      { label: "Estado_pedido", key: "estado" },
      { label: "Nombre_cliente", key: "nombre" },
      { label: "Teléfono_cliente", key: "telefono" },
      { label: "Correo_cliente", key: "correo" },
      { label: "Direccion_cliente", key: "direccion" },
      { label: "Depto_casa", key: "depto" },
      { label: "Comuna", key: "comuna" },
      { label: "Bultos_pedido", key: "bultos" },
      { label: "Nota_de_entrega", key: "nota" }
    ];

    const data = [];

    const parseDireccion = direccion => {
      if (!direccion) return "";

      const name = direccion.addressName ? direccion.addressName : "";
      const number = direccion.addressNumber ? ` ${direccion.addressNumber}` : "";
      const commune = direccion.addressComuna ? `, ${direccion.addressComuna}` : "";

      return `${name}${number}${commune}`;
    };

    tickets.map(ticket => {
      const direccion =
        ticket.body_ticket.cliente.direccion && JSON.parse(ticket.body_ticket.cliente.direccion);

      return data.push({
        id: ticket.folio_ticket,
        fecha: new Date(ticket.fecha_creacion_ticket).toLocaleString("es-CL"),
        estado: ticket.tipo_ticket.pipeline.pasos_pipeline[ticket.estado_ticket],
        nombre: ticket.contacto.nombre_contacto,
        telefono: ticket.contacto.body_contacto.telefono,
        correo: ticket.contacto.body_contacto.email,
        direccion: parseDireccion(direccion),
        depto: ticket.body_ticket.cliente.direccion2 ? ticket.body_ticket.cliente.direccion2 : "",
        comuna: direccion && direccion.addressComuna ? direccion.addressComuna : "",
        bultos: ticket.body_ticket.carrito.items.length,
        nota: ticket.body_ticket.carrito.delivery
          ? ticket.body_ticket.carrito.delivery.details.deliveryNote
          : ""
      });
    });

    const csvReport = {
      data: data,
      headers: headers,
      filename: `Reporte_pedidos_${tag}-${store.slug}-${new Date().toLocaleDateString("es-CL")}.csv`
    };

    return (
      <CSVLink {...csvReport} className={classes.appButton}>
        <GetAppIcon />
      </CSVLink>
    );
  };

  return (
    <div>
      <SearchDialog />

      {currentAppBar()}

      <div className={classes.container}>
        {fetching ? (
          <div className={classes.empty}>
            <CircularProgress color="primary" />
          </div>
        ) : (
          <>
            {(searchFolio || searchNombre) && (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" onClick={resetSearch}>
                    Cancelar
                  </Button>
                }
              >
                Búsqueda activa "{searchFolio}
                {searchNombre}"
              </Alert>
            )}
            {tickets.length === 0 ? (
              <div className={classes.empty}>
                <Typography color="textSecondary">No hay pedidos</Typography>
              </div>
            ) : (
              <Paper square>
                <List disablePadding>
                  {tickets.map((ticket, i) => {
                    const { pipeline } = ticket.tipo_ticket;
                    const date = parseJSON(ticket.fecha_creacion_ticket);
                    const today = isToday(date);

                    const TicketIcon = getEstadoInfo(ticket.estado_ticket).icon;

                    return (
                      <ListItem
                        key={`pedido-${i}`}
                        button
                        divider
                        selected={selected && ticket.id_ticket === selected.id_ticket}
                        onClick={() => onSelect(ticket)}
                      >
                        <ListItemIcon>
                          <TicketIcon color="primary" fontSize="large" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <>
                              <Typography component="span">#{ticket.folio_ticket}</Typography>
                              <Typography className="ml-2" component="span" color="textSecondary">
                                {ticket.contacto
                                  ? ticket.contacto.nombre_contacto
                                  : ticket.nombre_ticket}
                              </Typography>
                            </>
                          }
                          secondary={
                            <>
                              {`${ticket.body_ticket.carrito.items.length} producto${
                                ticket.body_ticket.carrito.items.length === 1 ? "" : "s"
                              }`}
                              <br />
                              {pipeline.pasos_pipeline[ticket.estado_ticket]}
                            </>
                          }
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                        <ListItemSecondaryAction onClick={() => onSelect(ticket)}>
                          <div className={classes.secondaryAction}>
                            <div className={classes.secondaryText}>
                              <br />
                              <Typography component="span" variant="caption" color="textSecondary">
                                {today && "Hoy, "}
                                {format(date, today ? "p" : "dd MMM, p", {
                                  locale: es
                                })}
                              </Typography>
                              <br />
                              <Typography component="span" variant="caption" color="textSecondary">
                                <NumberFormat
                                  value={ticket.body_ticket.pago.total || 0}
                                  thousandSeparator="."
                                  decimalSeparator=","
                                  prefix="$"
                                  displayType={"text"}
                                />
                              </Typography>
                            </div>
                            <ChevronRightIcon color="primary" />
                          </div>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PedidosList;
